import moment from 'moment';

import { getTenantConfigWithCache } from '@/api/cacheRequest';

import Storage from './storage';

export interface IntlBaseConfig {
  Language: string; // 语言编码
  TimeZone: number; // 时区ID
  TimeZoneCode: string; // 时区编码
  DSTStatus: boolean; // 夏令时
}

export interface TenantSignConfig {
  marchantName: string; // 商户名称(简称)eg:网鱼电竞酒店
  slogan: string; // 商户口号(eg: 高端玩家 都来网鱼)
  serviceTel: string; // 客服电话
  joinTel: string; // 加盟电话
  icon1x: string; // 1x图标-商户logo
  icon2x: string; // 2x图标-商户logo
  icon3x: string; // 3x图标-商户logo
}

const IntlGlobalConfig: IntlBaseConfig = {
  Language: null,
  TimeZone: null,
  TimeZoneCode: null,
  DSTStatus: false,
};

const TenantSignGlobalConfig: TenantSignConfig = {
  marchantName: null,
  slogan: null,
  serviceTel: null,
  joinTel: null,
  icon1x: null,
  icon2x: null,
  icon3x: null,
};

// 商户标识
const TenantSignKey = 'INTL_TENANT_SIGN_CONFIG';
export const TenantSignStorage = {
  getLocalConfig: () => {
    if (TenantSignGlobalConfig.marchantName) return TenantSignGlobalConfig;
    return JSON.parse(Storage.get(TenantSignKey) || '{}') as TenantSignConfig;
  },
  setLocalConfig: (config: Partial<TenantSignConfig>) => {
    Object.keys(config).forEach((itemKey) => {
      TenantSignGlobalConfig[itemKey] = config[itemKey];
    });
    Storage.set(TenantSignKey, TenantSignGlobalConfig);
  },
};

// 缓存商户标识配置
interface IConfigListItem {
  language: string;
  localeName: string;
  metadataCode: string;
  metadataName: string;
  metadataValue: string;
  required: boolean;
  tenantCode: string;
}
const tenantSignConfigCache = (configList: Array<IConfigListItem>) => {
  if (!configList || configList.length === 0) return;
  const params: any = {};
  params.marchantName = configList.filter(
    (item) => item.metadataCode === 'MERCHANT_SIMPLE_NAME',
  )[0]?.metadataValue;
  params.slogan = configList.filter((item) => item.metadataCode === 'SLOGAN')[0]?.metadataValue;
  params.serviceTel = configList.filter(
    (item) => item.metadataCode === 'C_SERVICE_TEL',
  )[0]?.metadataValue;
  params.joinTel = configList.filter(
    (item) => item.metadataCode === 'JOIN_HOTLINE',
  )[0]?.metadataValue;
  params.icon1x = configList.filter((item) => item.metadataCode === 'LOGO_SMALL')[0]?.metadataValue;
  params.icon2x = configList.filter(
    (item) => item.metadataCode === 'LOGO_MEDIUM',
  )[0]?.metadataValue;
  params.icon3x = configList.filter((item) => item.metadataCode === 'LOGO_BIG')[0]?.metadataValue;
  TenantSignStorage.setLocalConfig(params);
};

// 用户国际化本地配置
const UserConfigKey = 'INTL_USER_SETTING_CONFIG';
export const UserIntlStorage = {
  getLocalConfig: () => {
    if (IntlGlobalConfig.Language) return IntlGlobalConfig;
    return JSON.parse(Storage.get(UserConfigKey) || '{}') as IntlBaseConfig;
  },
  setLocalConfig: (config: Partial<IntlBaseConfig>) => {
    Object.keys(config).forEach((itemKey) => {
      IntlGlobalConfig[itemKey] = config[itemKey];
    });
    Storage.set(UserConfigKey, IntlGlobalConfig);
  },
};

export const getTimeByUtcOffset = (date?, formatStr?) => {
  if (formatStr) {
    if (!date) {
      return '-';
    }
    return moment(date).format(formatStr);
  }
  return date ? moment(date) : moment();
};

// 校验完整数据
export const checkConfigIntegrity = (config) => {
  const val = config || {};
  const integrityKeys: Array<keyof IntlBaseConfig> = ['Language', 'TimeZone', 'TimeZoneCode'];
  return integrityKeys.every((item) => val[item]);
};

// 当前国际化配置
export const getCurrentIntlConfig: () => Promise<IntlBaseConfig> = () => {
  const tenantQueue = getTenantConfigWithCache()
    .then((res) => {
      if (res && res.data) {
        const configVal = res.data;
        const updateVal: IntlBaseConfig = {
          Language: configVal.language,
          TimeZone: configVal.timeZoneId,
          TimeZoneCode: configVal.timeZoneCode,
          DSTStatus: false,
        };
        // 缓存商户标识
        tenantSignConfigCache(res?.data?.configList);
        return updateVal;
      }
      return null;
    })
    .catch(() => null);

  // 本地状态
  if (checkConfigIntegrity(IntlGlobalConfig)) {
    return Promise.resolve(IntlGlobalConfig);
  }

  // 缓存状态
  const localVal = UserIntlStorage.getLocalConfig();
  if (checkConfigIntegrity(localVal)) {
    return Promise.resolve(localVal);
  }

  // 租户配置
  return tenantQueue;
};
